import React, { useCallback, useEffect, useState } from "react";
import { socket } from "./socket";
import "./App.css";
import "./styles/operations-ui.css";
import AgentPage from "./pages/AgentPage";
import AdminPage from "./pages/AdminPage";
import AdminHome from "./pages/AdminHome";
import KpiDashboardPage from "./pages/KpiDashboardPage";
import KpiConfigurationPage from "./pages/KpiConfigurationPage";
import KpiDailyPage from "./pages/KpiDailyPage";
import KpiMonthlyPage from "./pages/KpiMonthlyPage";
import KpiInsightsPage from "./pages/KpiInsightsPage";
import KpiQuarterWorkspace from "./pages/KpiQuarterWorkspace";
import KpiTlFeedbackPage from "./pages/KpiTlFeedbackPage";
import { API_URL } from "./config";
import { apiRequest, formatApiError } from "./apiRequest";
import SessionAccessGate from "./components/SessionAccessGate";
import AppLoader from "./components/AppLoader";
import { openKpiSsoHandoff } from "./kpiSsoHandoff";

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.log("Dashboard render error:", error);
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1>Dashboard session needs refresh</h1>
          <p className="subtitle">
            Please reload once. If it keeps happening, sign in again.
          </p>
          <button
            className="logout-btn"
            onClick={() => {
              localStorage.removeItem("token");
              localStorage.removeItem("user");
              window.location.reload();
            }}
          >
            Refresh Login
          </button>
        </div>
      </div>
    );
  }
}

function App() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("user")) || null; }
    catch { return null; }
  });

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [adminModule, setAdminModule] = useState("home");
  const [agentModule, setAgentModule] = useState("portal");
  const [quarterAccess, setQuarterAccess] = useState(null);
  const [quarterAccessError, setQuarterAccessError] = useState(false);
  const [quarterAccessAttempt, setQuarterAccessAttempt] = useState(0);
  const [kpiSsoError, setKpiSsoError] = useState("");
  const openKpiView = (view) => setAdminModule(view === "overview" ? "kpi" : `kpi-${view}`);
  const selectHomeWorkspace = (workspace) => {
    if (workspace === "kpi") {
      setKpiSsoError("");
      openKpiSsoHandoff({ windowRef: window, onError: setKpiSsoError });
      return;
    }
    setKpiSsoError("");
    setAdminModule(workspace);
  };
  const syncSessionUser = useCallback((sessionUser) => {
    setUser((current) => {
      if (!current || current.id !== sessionUser.id) return current;
      const fields = ["name", "role", "roleVersion", "dsrRole", "team"];
      if (fields.every((field) => current[field] === sessionUser[field])) return current;
      const updated = { ...current, ...sessionUser };
      localStorage.setItem("user", JSON.stringify(updated));
      return updated;
    });
  }, []);

  useEffect(() => {
    let active = true;
    setQuarterAccess(null);
    setQuarterAccessError(false);
    const load = async () => {
      try {
        const { data } = await apiRequest(`${API_URL}/kpi-quarterly/access`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` }
        }, { retries: 0, timeoutMs: 10000 });
        if (active) {
          if (!data || typeof data.enabled !== "boolean") {
            setQuarterAccessError(true);
            return;
          }
          setQuarterAccess(data);
        }
      } catch {
        // This additive capability check must never leave a role on a permanent loader.
        if (active) setQuarterAccessError(true);
      }
    };
    if (user) void load();
    return () => { active = false; };
  }, [user, quarterAccessAttempt]);

  const retryQuarterAccess = () => {
    setQuarterAccessError(false);
    setQuarterAccessAttempt((attempt) => attempt + 1);
  };

  const quarterAccessFailure = (onBack) => (
    <main className="session-access-page">
      <section className="session-access-message" role="alert">
        <h1>KPI access could not be checked</h1>
        <p>Your access has not been changed. Retry the check or return to the previous screen.</p>
        <button type="button" onClick={retryQuarterAccess}>Retry access check</button>
        <button type="button" className="logout-btn" onClick={onBack}>Back</button>
      </section>
    </main>
  );

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetName, setResetName] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [showAgentSignup, setShowAgentSignup] = useState(false);
  const [signupName, setSignupName] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupZoomEmail, setSignupZoomEmail] = useState("");
  const [signupZoomExtension, setSignupZoomExtension] = useState("");
  const [signupCode, setSignupCode] = useState("");
  const [signupMessage, setSignupMessage] = useState("");
  const [signupLoading, setSignupLoading] = useState(false);

  const login = async () => {
    if (loginLoading) return;
    setLoginLoading(true);
    setLoginError("");
    try {
      const { data } = await apiRequest(
        `${API_URL}/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            name,
            password
          })
        },
        { retries: 1, timeoutMs: 15000 }
      );

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      setAdminModule("home");
      setAgentModule("portal");
      setUser(data.user);
    } catch (err) {
      setLoginError(formatApiError(err, "Login failed"));
    } finally {
      setLoginLoading(false);
    }
  };

  const submitPasswordResetRequest = async () => {
    if (!resetName || !resetPassword) {
      setResetMessage("Please fill username and new password");
      return;
    }

    setResetLoading(true);
    setResetMessage("");

    try {
      const { data } = await apiRequest(
        `${API_URL}/password-reset-requests`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            name: resetName,
            password: resetPassword
          })
        },
        { retries: 1, timeoutMs: 12000 }
      );

      setResetMessage(data.message || "Reset request sent to admin");
      setResetName("");
      setResetPassword("");
    } catch (err) {
      setResetMessage(formatApiError(err, "Failed to send reset request"));
    } finally {
      setResetLoading(false);
    }
  };

  const submitAgentSignup = async () => {
    if (
      !signupName ||
      !signupPassword ||
      !signupZoomEmail ||
      !signupZoomExtension ||
      !signupCode
    ) {
      setSignupMessage("Please fill all signup fields");
      return;
    }

    setSignupLoading(true);
    setSignupMessage("");

    try {
      const { data } = await apiRequest(
        `${API_URL}/agent-signup`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            name: signupName,
            password: signupPassword,
            zoomEmail: signupZoomEmail,
            zoomExtension: signupZoomExtension,
            signupCode
          })
        },
        { retries: 1, timeoutMs: 15000 }
      );

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      setUser(data.user);
    } catch (err) {
      setSignupMessage(formatApiError(err, "Failed to create account"));
    } finally {
      setSignupLoading(false);
    }
  };

const logout = async () => {
  try {
    if (user?.role === "agent" && user?.name) {
      await apiRequest(
        `${API_URL}/update-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`
          },
          body: JSON.stringify({
            name: user.name,
            status: "Logged Out"
          })
        },
        { retries: 1, timeoutMs: 8000 }
      );
    }
  } catch (err) {
    console.log(err);
  }

  localStorage.removeItem("token");
  localStorage.removeItem("user");
  socket.disconnect();

  setUser(null);
  setName("");
  setPassword("");
};

  const updateStatus = async (status, breakType = null) => {
    if (
  user?.status === status &&
  (status === "Break" || status === "Meeting")
) {
  alert(`Already on ${status}`);
  return user;
}
    try {
      const { data } = await apiRequest(
        `${API_URL}/update-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`
          },
          body: JSON.stringify({
            name: user.name,
            status,
            breakType
          })
        },
        { retries: 3, timeoutMs: 15000 }
      );

      return data.user;
    } catch (err) {
      if (err.data?.code === "MOBILE_ACCESS_BLOCKED") throw err;
      if (err.status === 401 || err.status === 403) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setUser(null);
        throw new Error("Session expired. Please login again.");
      }

      throw new Error(formatApiError(err, "Failed to update status"));
    }
  };

  const leaveSession = () => {
    // Leaving a blocked phone must not mark an active desktop session Logged Out.
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    socket.disconnect();
    setUser(null);
    setName("");
    setPassword("");
    setLoginError("");
  };

  if (loginLoading) return <AppLoader />;

  if (!user) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background:
            "radial-gradient(circle at top, rgba(37,99,235,0.22), #020617)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px"
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "460px",
            background: "rgba(15,23,42,0.96)",
            border: "1px solid rgba(148,163,184,0.18)",
            borderRadius: "30px",
            padding: "40px",
            boxShadow: "0 0 50px rgba(0,0,0,0.45)",
            backdropFilter: "blur(12px)"
          }}
        >
          {loginError && <p role="alert" style={{ color: "#fca5a5" }}>{loginError}</p>}
          <div style={{ textAlign: "center", marginBottom: "34px" }}>
            <div
              style={{
                fontSize: "62px",
                marginBottom: "12px"
              }}
            >
              📊
            </div>

            <h1
              style={{
                color: "#fff",
                margin: 0,
                fontSize: "36px"
              }}
            >
              Sales Floor
            </h1>

            <p
              style={{
                color: "#94a3b8",
                marginTop: "10px",
                fontSize: "16px"
              }}
            >
              Live Dashboard Monitoring System
            </p>
          </div>

          <div
            style={{
              background: "rgba(30,41,59,0.65)",
              padding: "26px",
              borderRadius: "22px",
              border: "1px solid rgba(148,163,184,0.12)"
            }}
          >
            <h2
              style={{
                color: "#fff",
                marginTop: 0,
                marginBottom: "24px",
                fontSize: "28px",
                textAlign: "center"
              }}
            >
              {showAgentSignup
                ? "Agent Sign Up"
                : showForgotPassword
                  ? "Reset Password"
                  : "Secure Login"}
            </h2>

            {showAgentSignup ? (
              <>
                <input
                  className="input"
                  type="text"
                  placeholder="Full name"
                  value={signupName}
                  onChange={(e) => setSignupName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "16px",
                    marginBottom: "16px",
                    borderRadius: "14px",
                    border: "1px solid rgba(148,163,184,0.2)",
                    background: "#0f172a",
                    color: "#fff",
                    fontSize: "16px",
                    boxSizing: "border-box"
                  }}
                />

                <input
                  className="input"
                  type="password"
                  placeholder="Create password"
                  value={signupPassword}
                  onChange={(e) => setSignupPassword(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "16px",
                    marginBottom: "16px",
                    borderRadius: "14px",
                    border: "1px solid rgba(148,163,184,0.2)",
                    background: "#0f172a",
                    color: "#fff",
                    fontSize: "16px",
                    boxSizing: "border-box"
                  }}
                />

                <input
                  className="input"
                  type="email"
                  placeholder="Zoom email"
                  value={signupZoomEmail}
                  onChange={(e) => setSignupZoomEmail(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "16px",
                    marginBottom: "16px",
                    borderRadius: "14px",
                    border: "1px solid rgba(148,163,184,0.2)",
                    background: "#0f172a",
                    color: "#fff",
                    fontSize: "16px",
                    boxSizing: "border-box"
                  }}
                />

                <input
                  className="input"
                  type="text"
                  placeholder="Zoom extension"
                  value={signupZoomExtension}
                  onChange={(e) => setSignupZoomExtension(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "16px",
                    marginBottom: "16px",
                    borderRadius: "14px",
                    border: "1px solid rgba(148,163,184,0.2)",
                    background: "#0f172a",
                    color: "#fff",
                    fontSize: "16px",
                    boxSizing: "border-box"
                  }}
                />

                <input
                  className="input"
                  type="password"
                  placeholder="Team signup code"
                  value={signupCode}
                  onChange={(e) => setSignupCode(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "16px",
                    marginBottom: "16px",
                    borderRadius: "14px",
                    border: "1px solid rgba(148,163,184,0.2)",
                    background: "#0f172a",
                    color: "#fff",
                    fontSize: "16px",
                    boxSizing: "border-box"
                  }}
                />

                <p
                  style={{
                    color: "#94a3b8",
                    lineHeight: 1.5,
                    marginTop: 0,
                    marginBottom: "16px"
                  }}
                >
                  Use the same name your manager expects to see on the dashboard.
                </p>

                {signupMessage && (
                  <p
                    style={{
                      color: signupMessage.includes("created")
                        ? "#22c55e"
                        : "#fca5a5",
                      marginTop: 0,
                      marginBottom: "16px",
                      lineHeight: 1.5
                    }}
                  >
                    {signupMessage}
                  </p>
                )}

                <button
                  onClick={submitAgentSignup}
                  disabled={signupLoading}
                  style={{
                    width: "100%",
                    background:
                      "linear-gradient(135deg, #2563eb, #38bdf8)",
                    color: "#fff",
                    border: "none",
                    padding: "16px",
                    borderRadius: "14px",
                    fontWeight: "bold",
                    fontSize: "16px",
                    cursor: signupLoading ? "not-allowed" : "pointer",
                    marginBottom: "14px",
                    opacity: signupLoading ? 0.75 : 1
                  }}
                >
                  {signupLoading ? "Creating..." : "Create Agent Account"}
                </button>

                <button
                  onClick={() => {
                    setShowAgentSignup(false);
                    setSignupMessage("");
                    setSignupName("");
                    setSignupPassword("");
                    setSignupZoomEmail("");
                    setSignupZoomExtension("");
                    setSignupCode("");
                  }}
                  style={{
                    width: "100%",
                    background: "#1e293b",
                    color: "#fff",
                    border: "1px solid rgba(148,163,184,0.18)",
                    padding: "16px",
                    borderRadius: "14px",
                    fontWeight: "bold",
                    fontSize: "15px",
                    cursor: "pointer"
                  }}
                >
                  Back to Login
                </button>
              </>
            ) : showForgotPassword ? (
              <>
                <input
                  className="input"
                  type="text"
                  placeholder="Enter Username"
                  value={resetName}
                  onChange={(e) => setResetName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "16px",
                    marginBottom: "16px",
                    borderRadius: "14px",
                    border: "1px solid rgba(148,163,184,0.2)",
                    background: "#0f172a",
                    color: "#fff",
                    fontSize: "16px",
                    boxSizing: "border-box"
                  }}
                />

                <input
                  className="input"
                  type="password"
                  placeholder="New Password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "16px",
                    marginBottom: "16px",
                    borderRadius: "14px",
                    border: "1px solid rgba(148,163,184,0.2)",
                    background: "#0f172a",
                    color: "#fff",
                    fontSize: "16px",
                    boxSizing: "border-box"
                  }}
                />

                {resetMessage && (
                  <p
                    style={{
                      color: resetMessage.includes("successfully")
                        ? "#22c55e"
                        : "#bae6fd",
                      marginTop: 0,
                      marginBottom: "16px",
                      lineHeight: 1.5
                    }}
                  >
                    {resetMessage}
                  </p>
                )}

                <button
                  onClick={submitPasswordResetRequest}
                  disabled={resetLoading}
                  style={{
                    width: "100%",
                    background:
                      "linear-gradient(135deg, #2563eb, #38bdf8)",
                    color: "#fff",
                    border: "none",
                    padding: "16px",
                    borderRadius: "14px",
                    fontWeight: "bold",
                    fontSize: "16px",
                    cursor: resetLoading ? "not-allowed" : "pointer",
                    marginBottom: "14px",
                    opacity: resetLoading ? 0.75 : 1
                  }}
                >
                  {resetLoading ? "Please wait..." : "Send Request"}
                </button>

                <button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetMessage("");
                    setResetName("");
                    setResetPassword("");
                  }}
                  style={{
                    width: "100%",
                    background: "#1e293b",
                    color: "#fff",
                    border: "1px solid rgba(148,163,184,0.18)",
                    padding: "16px",
                    borderRadius: "14px",
                    fontWeight: "bold",
                    fontSize: "15px",
                    cursor: "pointer"
                  }}
                >
                  Back to Login
                </button>
              </>
            ) : (
              <>
                <input
                  className="input"
                  type="text"
                  placeholder="Enter Username"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "16px",
                    marginBottom: "16px",
                    borderRadius: "14px",
                    border: "1px solid rgba(148,163,184,0.2)",
                    background: "#0f172a",
                    color: "#fff",
                    fontSize: "16px",
                    boxSizing: "border-box"
                  }}
                />

                <input
                  className="input"
                  type="password"
                  placeholder="Enter Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "16px",
                    marginBottom: "20px",
                    borderRadius: "14px",
                    border: "1px solid rgba(148,163,184,0.2)",
                    background: "#0f172a",
                    color: "#fff",
                    fontSize: "16px",
                    boxSizing: "border-box"
                  }}
                />

                <button
                  onClick={login}
                  style={{
                    width: "100%",
                    background:
                      "linear-gradient(135deg, #2563eb, #38bdf8)",
                    color: "#fff",
                    border: "none",
                    padding: "16px",
                    borderRadius: "14px",
                    fontWeight: "bold",
                    fontSize: "16px",
                    cursor: "pointer",
                    marginBottom: "14px"
                  }}
                >
                  Login
                </button>

                <button
                  onClick={() => {
                    setShowForgotPassword(true);
                    setShowAgentSignup(false);
                    setResetMessage("");
                    setResetName("");
                    setResetPassword("");
                  }}
                  style={{
                    width: "100%",
                    background: "transparent",
                    color: "#38bdf8",
                    border: "none",
                    padding: "8px",
                    fontWeight: "bold",
                    fontSize: "14px",
                    cursor: "pointer",
                    marginBottom: "12px"
                  }}
                >
                  Forgot Password?
                </button>

                <button
                  onClick={() => {
                    setShowAgentSignup(true);
                    setShowForgotPassword(false);
                    setSignupMessage("");
                  }}
                  style={{
                    width: "100%",
                    background: "#1e293b",
                    color: "#fff",
                    border: "1px solid rgba(148,163,184,0.18)",
                    padding: "14px",
                    borderRadius: "14px",
                    fontWeight: "bold",
                    fontSize: "15px",
                    cursor: "pointer"
                  }}
                >
                  New Agent? Create Account
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (user.role === "admin") {
    return (
      <AppErrorBoundary>
        <SessionAccessGate onSignOut={leaveSession} onSession={syncSessionUser}>
          {adminModule === "home" ? (
            <AdminHome user={user} onSelect={selectHomeWorkspace} onLogout={logout} kpiSsoError={kpiSsoError} />
          ) : quarterAccess?.enabled && (adminModule === "kpi" || (adminModule.startsWith("kpi-") && !["kpi-config", "kpi-legacy-monthly", "kpi-legacy-history"].includes(adminModule))) ? (
            <KpiQuarterWorkspace mode={adminModule === "kpi" ? "overview" : adminModule.replace("kpi-", "")} onHome={() => setAdminModule("home")} onNavigate={openKpiView} />
          ) : adminModule === "kpi-legacy-monthly" ? (
            <KpiMonthlyPage onHome={() => setAdminModule("home")} onNavigate={openKpiView} />
          ) : adminModule === "kpi-legacy-history" ? (
            <KpiDailyPage mode="history" onHome={() => setAdminModule("home")} onNavigate={openKpiView} />
          ) : adminModule === "kpi" ? (
            <KpiDashboardPage onHome={() => setAdminModule("home")} onNavigate={openKpiView} />
          ) : adminModule === "kpi-monthly" ? (
            <KpiMonthlyPage onHome={() => setAdminModule("home")} onNavigate={openKpiView} />
          ) : ["kpi-quarterly", "kpi-leaderboard", "kpi-reports"].includes(adminModule) ? (
            <KpiInsightsPage mode={adminModule.replace("kpi-", "")} onHome={() => setAdminModule("home")} onNavigate={openKpiView} />
          ) : adminModule === "kpi-config" ? (
            <KpiConfigurationPage onHome={() => setAdminModule("home")} onNavigate={openKpiView} canBootstrapCatalog={user.role === "admin"} canApplyQuarterlyTemplate={user.role === "admin"} />
          ) : adminModule === "kpi-daily-entry" || adminModule === "kpi-daily-history" ? (
            <KpiDailyPage key={adminModule} mode={adminModule === "kpi-daily-entry" ? "entry" : "history"} onHome={() => setAdminModule("home")} onNavigate={openKpiView} />
          ) : (
            <AdminPage
              logout={logout}
              onHome={() => setAdminModule("home")}
              initialOpenDsr={adminModule === "dsr"}
            />
          )}
        </SessionAccessGate>
      </AppErrorBoundary>
    );
  }

  if (user.role === "tv") {
    return (
      <AppErrorBoundary>
        <SessionAccessGate onSignOut={leaveSession} onSession={syncSessionUser}><AdminPage viewerMode logout={logout} /></SessionAccessGate>
      </AppErrorBoundary>
    );
  }

  if (["am", "sm"].includes(user.role)) {
    return (
      <AppErrorBoundary>
        <SessionAccessGate onSignOut={leaveSession} onSession={syncSessionUser}>
          {adminModule === "home" ? <AdminHome user={user} onSelect={selectHomeWorkspace} onLogout={logout} kpiSsoError={kpiSsoError} /> :
            quarterAccessError && adminModule === "kpi" ? quarterAccessFailure(() => setAdminModule("home")) :
            quarterAccess?.enabled && (adminModule === "kpi" || (adminModule.startsWith("kpi-") && adminModule !== "kpi-config")) ? (
              <KpiQuarterWorkspace mode={adminModule === "kpi" ? "overview" : adminModule.replace("kpi-", "")} onHome={() => setAdminModule("home")} onNavigate={openKpiView} backLabel="Home" />
            ) : quarterAccess?.enabled && adminModule === "kpi-config" ? (
              <KpiConfigurationPage onHome={() => setAdminModule("home")} onNavigate={openKpiView} canBootstrapCatalog={false} canApplyQuarterlyTemplate={false} />
            ) : adminModule === "kpi" ? (
              <main className="session-access-page"><section className="session-access-message" role="status"><h1>KPI workspace unavailable</h1><p>Ask an administrator to verify the quarterly KPI setup.</p><button className="logout-btn" onClick={() => setAdminModule("home")}>Home</button></section></main>
            ) : <AdminPage operationalManagerMode logout={logout} onHome={() => setAdminModule("home")} initialOpenDsr={adminModule === "dsr"} />}
        </SessionAccessGate>
      </AppErrorBoundary>
    );
  }

  if (user.role === "qa") {
    return (
      <AppErrorBoundary>
        <SessionAccessGate onSignOut={leaveSession} onSession={syncSessionUser}>
          {quarterAccessError ? quarterAccessFailure(logout) : quarterAccess?.enabled ? (
          <KpiQuarterWorkspace mode="monthly" onHome={logout} backLabel="Sign out" />
          ) : quarterAccess ? (
            <main className="session-access-page"><section className="session-access-message" role="status"><h1>KPI workspace unavailable</h1><p>Ask an administrator to verify the quarterly KPI setup.</p><button className="logout-btn" onClick={logout}>Sign out</button></section></main>
          ) : (
            <AppLoader />
          )}
        </SessionAccessGate>
      </AppErrorBoundary>
    );
  }

  const isTeamLead = user.role === "agent" && ["tl", "team lead", "team leader"].includes(String(user.dsrRole || user.dsr_role || "").toLowerCase().trim());
  if (quarterAccess?.enabled && agentModule.startsWith("kpi-")) {
    if (isTeamLead && !quarterAccess.canRead) return <AppErrorBoundary><SessionAccessGate onSignOut={leaveSession} onSession={syncSessionUser}><KpiTlFeedbackPage onHome={() => setAgentModule("portal")} /></SessionAccessGate></AppErrorBoundary>;
    return <AppErrorBoundary><SessionAccessGate onSignOut={leaveSession} onSession={syncSessionUser}><KpiQuarterWorkspace mode={quarterAccess.canRead ? "overview" : "profile"} sharedPeriods={quarterAccess.sharedPeriods || []} onHome={() => setAgentModule("portal")} backLabel="Agent Portal" /></SessionAccessGate></AppErrorBoundary>;
  }
  if (agentModule === "kpi-profile") {
    return <AppErrorBoundary><SessionAccessGate onSignOut={leaveSession} onSession={syncSessionUser}><KpiInsightsPage mode="profile" onBack={() => setAgentModule("portal")} /></SessionAccessGate></AppErrorBoundary>;
  }

  return (
    <AppErrorBoundary>
      <SessionAccessGate onSignOut={leaveSession} onSession={syncSessionUser}><AgentPage
        agentName={user.name}
        logout={logout}
        updateStatus={updateStatus}
        onOpenKpi={isTeamLead && quarterAccess?.canEnterFeedback ? () => setAgentModule("kpi-tl-feedback") : undefined}
        onOpenKpiProfile={user.role === "agent" && !isTeamLead && quarterAccess?.enabled && !quarterAccess.canRead ? () => setAgentModule("kpi-profile") : undefined}
      /></SessionAccessGate>
    </AppErrorBoundary>
  );
}

export default App;
